/**
 * Spanish Home Translations
 * 首页翻译模块 - 西班牙语
 * 
 * SEO 关键词覆盖：
 * - calefactor infrarrojo / fabricante / mayorista
 * - calefacción radiante / eficiencia energética
 * - calefactor de terraza / restaurante / taller
 */

import type { HomeTranslations } from '../../types';

export const esHome: HomeTranslations = {
  hero: {
    future: 'El Futuro de la Calefacción',
    title: 'Eficiente y Ecológico',
    subtitle: 'Calefacción Infrarroja',
    description: 'Lleva un calor similar al del sol a los espacios exteriores. Nuestra avanzada tecnología de infrarrojo lejano ofrece calor radiante instantáneo, silencioso y con alta eficiencia energética para terrazas de restaurantes, jardines, talleres y zonas de piscina.',
    cta: 'Explorar Productos',
    watchVideo: 'Ver Video',
    trusted: 'Confiado por más de 5,000 clientes',
    worldwide: 'Distribución mundial',
    slides: {
      "slide5": {
        "title": "Feria de Cantón",
        "subtitle": "Nos vemos en Guangzhou",
        "description": "Conozca a JH Heatsup en la Feria de Cantón. Descubra nuestras soluciones de calefacción y explore oportunidades de colaboración.",
        "exhibition": {
          "eyebrow": "JH HEATSUP · FERIA DE CANTÓN",
          "date": "15–19 de octubre de 2026",
          "venue": "Complejo de la Feria de Cantón, Guangzhou",
          "boothLabel": "VISITE NUESTRO STAND",
          "booth": "Pabellón 3.2 · Stand L16"
        }
      },
      "slide4": {
        "title": "Global Sources",
        "subtitle": "Electrónica de consumo",
        "description": "Conozca a JH Heatsup en Hong Kong. Descubra nuestras soluciones de calefacción y explore oportunidades de colaboración.",
        "exhibition": {
          "eyebrow": "JH HEATSUP · FERIA EN HONG KONG",
          "date": "11–14 de octubre de 2026",
          "venue": "AsiaWorld-Expo, Hong Kong",
          "boothLabel": "VISITE NUESTRO STAND"
        }
      },
      slide1: {
        title: 'Eficiente y Ecológico',
        subtitle: 'Calefacción Infrarroja',
        description: 'Lleva un calor similar al del sol a los espacios exteriores. Nuestra avanzada tecnología de infrarrojo lejano ofrece calor radiante instantáneo, silencioso y con alta eficiencia energética para terrazas de restaurantes, jardines, talleres y zonas de piscina.',
      },
      slide2: {
        title: 'Seguro y Fiable',
        subtitle: 'Diseñado para el uso diario',
        description: 'Diseñados con clasificación IPX4 a prueba de agua, protección contra sobrecalentamiento integrada y materiales duraderos. Nuestros calefactores infrarrojos ofrecen calor estable y fiable día tras día — seguros para funcionar toda la noche.',
      },
      slide3: {
        title: 'Ultra Silencioso',
        subtitle: 'Calor Cómodo',
        description: 'Disfruta de un calor radiante silencioso y sin brillo que se siente natural, sin resecar el aire ni circular polvo. Calefacción infrarroja saludable para hogares y oficinas.',
      },
    },
  },
  about: {
    title: 'Sobre JH Calefactor',
    subtitle: 'Liderando el Camino en Tecnología Infrarroja Desde 2006',
    intro1: 'Fujian Jinghui Environmental Technology Co., Ltd. (JH Heatsup) es un fabricante líder de calefactores infrarrojos en China, especializado en calefactores radiantes de infrarrojo lejano con alta eficiencia energética para uso residencial y comercial.',
    intro2: 'Nos enfocamos en I+D, fabricación OEM/ODM y distribución global para proporcionar calefactores infrarrojos de alta calidad a compradores mayoristas en todo el mundo. Nuestros productos cuentan con certificaciones CE, CB, SAA y RoHS, exportados a más de 80 países en Europa, Australia, Oriente Medio y Sudeste Asiático.',
    stats: {
      yearsExperience: 'Años de Experiencia',
      countriesExported: 'Países Exportados',
      patents: 'Patentes',
      satisfaction: 'Satisfacción',
    },
    ceoQuote: '"Estamos comprometidos a crear un mundo más verde y cálido a través de la tecnología."',
    ceoLabel: '- Mensaje del Director General',
    factoryAlt: 'Línea de producción de la fábrica de calefactores infrarrojos JH',
  },
  features: {
    title: 'Por Qué Elegir Calefactores Infrarrojos JH',
    subtitle: 'Nuestra tecnología de infrarrojo lejano imita el principio natural de calefacción del sol, calentando objetos y personas directamente en lugar de solo el aire. Esto hace que los calefactores infrarrojos sean hasta un 80% más eficientes energéticamente que los calefactores de convección tradicionales.',
    energy: {
      title: 'Eficiencia Energética',
      desc: 'Hasta 99% de eficiencia de conversión de calor — los calefactores radiantes infrarrojos usan hasta un 80% menos electricidad que los calefactores de convección',
    },
    quiet: {
      title: 'Ultra Silencioso',
      desc: 'Diseño infrarrojo sin ventilador, sin luz, sin ruido y sin circulación de polvo',
    },
    safe: {
      title: 'Seguro y Fiable',
      desc: 'Calefactores infrarrojos a prueba de agua IPX4 con protección contra sobrecalentamiento integrada, seguros para uso exterior e interior',
    },
    smart: {
      title: 'Control Inteligente',
      desc: 'Control por aplicación WiFi Tuya para programación remota de temperatura — ahorra energía desde tu teléfono',
    },
  },
  categoriesSection: {
    title: 'Compra por categoría',
    subtitle: "Explora nuestros calefactores infrarrojos, portátiles y de gas para encontrar la mejor opción para tu espacio, desde terrazas exteriores hasta salas de estar.",
    viewAll: 'Ver todos los productos',
    cards: {
      infrared: {
        badge: 'Popular',
        tagline: 'Calefactores radiantes de infrarrojo lejano para calor eficiente y dirigido',
        description: 'Ideal para terrazas de restaurantes, talleres, almacenes y calefacción interior montada en pared.',
        cta: 'Comprar ahora',
      },
      portable: {
        tagline: 'Calefactores portátiles de fibra de carbono y halógenos para calor flexible',
        description: 'Perfecto para hogares, oficinas, calefacción bajo escritorio y áreas de entretenimiento exterior.',
        cta: 'Comprar ahora',
      },
      gas: {
        tagline: "Calefactores de gas y mesas de fuego para espacios al aire libre",
        description: "Crea un ambiente acogedor en terrazas de restaurantes, jardines y reuniones en el patio.",
        cta: "Comprar ahora",
      },
    },
  },
  ctaSection: {
    title: '¿Listo para mejorar tu sistema de calefacción?',
    subtitle: 'Contacta a JH Heatsup hoy para precios mayoristas, servicios OEM/ODM y una consulta gratuita.',
    cta: 'Solicitar Cotización',
  },
};
